import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import type { QmsRegistryKind } from '../features/qms/qmsApi';
import { fetchQmsRecordDetail } from '../features/qms/qmsApi';
import { permissionKeyForKind } from '../features/qms/permissionKeys';
import {
  labelQmsAttributability,
  labelQmsOrigin,
  labelQmsRecordStatus,
  labelQmsReview,
  labelTypificationMode,
  qmsRecordStatusBadgeClass,
  qmsReviewBadgeClass,
} from '../features/qms/qmsLabels';
import { FormField, FormStructure } from '../features/surveys/types';
import { AttachmentsList } from '../components/AttachmentsList';

interface Props {
  kind: QmsRegistryKind;
  title: string;
  basePath: string;
}

function renderDynamicSummary(structure: unknown, data: Record<string, unknown>) {
  const s = structure as FormStructure | undefined;
  if (!s?.fields?.length) return null;
  return (
    <dl className="grid gap-2 text-sm">
      {s.fields.map((f: FormField) => (
        <div key={f.id} className="border-b border-border pb-2">
          <dt className="font-medium text-muted-foreground">{f.label}</dt>
          <dd className="mt-0.5 whitespace-pre-wrap break-words">
            {formatDynamicValue(data[f.id])}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function formatDynamicValue(v: unknown): string {
  if (v === undefined || v === null) return '—';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function QmsRecordDetailPage({ kind, title, basePath }: Props) {
  const { id } = useParams<{ id: string }>();
  const { canEdit } = usePlatformAccessLevel(permissionKeyForKind(kind));
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState('');

  const load = async () => {
    if (!id) return;
    try {
      const d = await fetchQmsRecordDetail(id);
      setData(d as Record<string, any>);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recargar solo al cambiar id (comportamiento preexistente)
  }, [id]);

  if (error) {
    return (
      <div className="container max-w-3xl py-8 px-4">
        <p className="text-destructive">{error}</p>
        <Link to={basePath} className="mt-4 inline-block text-primary">
          Volver
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container max-w-3xl py-8 px-4">
        <p className="text-muted-foreground">Cargando…</p>
      </div>
    );
  }

  if (data.scope === 'own_minimal') {
    return (
      <div className="container max-w-3xl py-8 px-4">
        <h1 className="text-xl font-semibold mb-4">{title}</h1>
        <p className="text-muted-foreground mb-2">Su registro fue recibido.</p>
        <p className="text-sm text-muted-foreground">
          Estado:{' '}
          <span className={qmsRecordStatusBadgeClass(data.status)}>{labelQmsRecordStatus(data.status)}</span>
        </p>
        <p className="mt-4 text-xs text-muted-foreground">
          No se muestran análisis ni seguimiento en esta vista.
        </p>
        <Link to={basePath} className="mt-6 inline-block text-primary">
          Volver
        </Link>
      </div>
    );
  }

  const opening = data;
  const timeline = (opening.timeline as { at: string; title: string; detail: string }[]) ?? [];

  return (
    <div className="container max-w-3xl py-8 px-4 space-y-8">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Detalle — {title}</h1>
          <p className="text-sm text-muted-foreground mt-2 flex flex-wrap items-center gap-2">
            <span className={qmsRecordStatusBadgeClass(opening.status)}>{labelQmsRecordStatus(opening.status)}</span>
            <span className={qmsReviewBadgeClass(opening.reviewOutcome)}>
              Revisión: {labelQmsReview(opening.reviewOutcome)}
            </span>
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Link to={basePath} className="text-sm text-primary hover:underline">
            Volver al listado
          </Link>
          {canEdit && (
            <Link
              to={`${basePath}/${opening.id}/planes`}
              className="inline-flex items-center rounded-md bg-secondary px-3 py-2 text-sm font-medium hover:bg-secondary/80"
            >
              Planes de acción
            </Link>
          )}
        </div>
      </div>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3 text-sm">
        <h2 className="font-semibold text-base">Datos del registro</h2>
        <dl className="grid gap-2 sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">Fecha del incidente</dt>
            <dd>{new Date(opening.incidentDate).toLocaleString()}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Origen</dt>
            <dd>{labelQmsOrigin(opening.originType)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Tipificación</dt>
            <dd>{labelTypificationMode(opening.typificationMode)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Atribuibilidad</dt>
            <dd>{labelQmsAttributability(opening.attributability)}</dd>
          </div>
        </dl>
        <div>
          <dt className="text-muted-foreground font-medium mb-1">Descripción</dt>
          <dd className="whitespace-pre-wrap">{opening.description}</dd>
        </div>
      </section>

      {opening.formVersion?.structure && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="font-semibold text-base mb-3">Campos adicionales</h2>
          {renderDynamicSummary(opening.formVersion.structure, (opening.dynamicData as Record<string, unknown>) ?? {})}
        </section>
      )}

      {id && (
        <section className="rounded-lg border border-border bg-card p-4">
          <AttachmentsList
            resourceType="qms_record"
            resourceId={id}
            readOnly={!canEdit}
            title="Evidencias"
            accept=".pdf,.doc,.docx,.zip,image/*,video/mp4"
          />
        </section>
      )}

      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="font-semibold text-base mb-3">Historial de cambios</h2>
        {timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin eventos registrados aún.</p>
        ) : (
          <ol className="space-y-4 border-l-2 border-primary/30 pl-4 ml-1">
            {timeline.map((ev, i) => (
              <li key={`${ev.at}-${i}`} className="relative">
                <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                <p className="text-xs text-muted-foreground">{new Date(ev.at).toLocaleString()}</p>
                <p className="font-medium text-sm">{ev.title}</p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{ev.detail}</p>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
